import { memo, useMemo } from 'react';
import type { RackingArea, SingularStationArea } from '@thive/fddm-types';
import { useShallow } from 'zustand/react/shallow';
import { Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Accordion } from '@common/components/Accordion';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { createLoadTypeDimensionsById } from '@/modules/floorplanDesign/helpers/stationCapacity';

import { AreaStationSummaryCard } from './AreaStationSummaryCard';
import {
  createAreaDetails,
  getCombinedStationCount,
  getUniqueStations,
} from './areaDetailsHelpers';
import { UserPreferenceName } from '@/modules/userPreferences';

type Props = {
  selectedSingularStationAreas: SingularStationArea[];
  selectedRackingAreas: RackingArea[];
};

function AreaDetailsComponent({ selectedSingularStationAreas, selectedRackingAreas }: Props) {
  const { t } = useTranslation('interface');
  const loadTypes = useFloorplanDesignStore((state) => state.setup.loadTypes);

  const selectedAreas = useMemo(
    () => [...selectedSingularStationAreas, ...selectedRackingAreas],
    [selectedSingularStationAreas, selectedRackingAreas],
  );
  const selectedStations = useFloorplanDesignStore(
    useShallow((state) =>
      getUniqueStations(state.getStationsByAreaIds(selectedAreas.map(({ id }) => id))),
    ),
  );
  const loadTypeDimensionsById = useMemo(
    () => createLoadTypeDimensionsById(loadTypes),
    [loadTypes],
  );
  const areaDetails = useMemo(
    () => createAreaDetails(selectedAreas, selectedStations, loadTypeDimensionsById),
    [selectedAreas, selectedStations, loadTypeDimensionsById],
  );

  const combinedStationCount = getCombinedStationCount(areaDetails);
  const selectedAreaCount = selectedAreas.length;
  const hasMultipleSelectedAreas = selectedAreaCount > 1;

  if (areaDetails.length === 0) return null;

  return (
    <>
      <Divider sx={{ pt: 1 }} />
      <Accordion
        title={`${t('interface:properties.area.details', 'Area details')} (${selectedAreaCount})`}
        persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_AREA_DETAILS}
        unmountOnExit
        sx={{ pt: 1, px: 2 }}
      >
        <Stack spacing={1} sx={{ pb: 1 }}>
          {hasMultipleSelectedAreas && (
            <Stack
              direction='row'
              alignItems='baseline'
              justifyContent='space-between'
              spacing={2}
              sx={{ pb: 1 }}
            >
              <Typography variant='body2' color='text.secondary'>
                Total stations
              </Typography>
              <Typography
                variant='body1'
                fontWeight={600}
                sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
              >
                {combinedStationCount}
              </Typography>
            </Stack>
          )}

          {areaDetails.map((areaDetail) => (
            <Stack key={areaDetail.id} spacing={1} sx={{ pb: 1 }}>
              {areaDetail.summaries.map((summary) => (
                <AreaStationSummaryCard
                  key={summary.id}
                  summary={summary}
                  color={areaDetail.color}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      </Accordion>
    </>
  );
}

export const AreaDetails = memo(AreaDetailsComponent);
